import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronDown, Search, X, Menu, User, LogOut, CheckCheck } from "lucide-react";
import { useAuth } from "../Auth-Context/AuthContext";
import { useNotifications } from "../hooks/useNotifications";

// ── Relative time helper ───────────────────────────────────────────────────
function relativeTime(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Route map ──────────────────────────────────────────────────────────────
const RESOURCE_ROUTES = {
  TASK: "/tasks",
  DEAL: "/pipeline",
};

// ── Notification Dropdown ──────────────────────────────────────────────────
function NotificationDropdown({ notifications, loading, onMarkRead, onMarkAllRead, onClose, navigate }) {
  const unreadNotifs = notifications.filter((n) => !n.read);

  const handleNotifClick = async (notif) => {
    if (!notif.read) await onMarkRead(notif.id);
    const route = RESOURCE_ROUTES[notif.resourceType];
    if (route) navigate(route);
    onClose();
  };

  return (
    <div
      className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border top-full rounded-2xl border-neutral-100"
      style={{
        width: "340px",
        boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-bold text-neutral-900">Notifications</span>
          {unreadNotifs.length > 0 && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white"
              style={{ background: "#009DB9" }}
            >
              {unreadNotifs.length}
            </span>
          )}
        </div>
        {unreadNotifs.length > 0 && (
          <button
            onClick={onMarkAllRead}
            className="flex items-center gap-1 text-[11px] font-semibold transition-colors text-neutral-400 hover:text-[#009DB9]"
          >
            <CheckCheck size={12} />
            Mark all read
          </button>
        )}
      </div>

      {/* Body */}
      <div className="overflow-y-auto max-h-[340px]">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-5 h-5 border-2 rounded-full border-b-transparent animate-spin" style={{ borderColor: "#009DB9", borderBottomColor: "transparent" }} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10">
            <Bell size={24} className="text-neutral-200" />
            <span className="text-[12px] text-neutral-400">No notifications</span>
          </div>
        ) : (
          notifications.map((notif) => (
            <button
              key={notif.id}
              onClick={() => handleNotifClick(notif)}
              className="flex items-start w-full gap-3 px-4 py-3 text-left transition-colors border-b border-neutral-100/80 hover:bg-neutral-50 last:border-b-0"
              style={
                !notif.read
                  ? { borderLeft: "3px solid #009DB9", background: "rgba(0,157,185,0.03)" }
                  : { borderLeft: "3px solid transparent", opacity: 0.65 }
              }
            >
              {/* Type dot */}
              <div
                className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5"
                style={{ background: notif.read ? "#d4d4d4" : "#009DB9" }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <span
                    className="text-[12px] font-semibold leading-snug text-neutral-800 truncate"
                    style={notif.read ? { color: "#a3a3a3" } : {}}
                  >
                    {notif.title}
                  </span>
                  <span className="text-[10px] text-neutral-400 flex-shrink-0 mt-0.5">
                    {relativeTime(notif.createdAt)}
                  </span>
                </div>
                {notif.message && (
                  <p
                    className="text-[11px] mt-0.5 leading-relaxed line-clamp-2"
                    style={{ color: notif.read ? "#d4d4d4" : "#737373" }}
                  >
                    {notif.message}
                  </p>
                )}
                {notif.resourceType && (
                  <span
                    className="inline-block mt-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                    style={{
                      background: notif.resourceType === "TASK" ? "#fef3c7" : "#dbeafe",
                      color: notif.resourceType === "TASK" ? "#d97706" : "#3b82f6",
                    }}
                  >
                    {notif.resourceType}
                  </span>
                )}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// ── Topbar ─────────────────────────────────────────────────────────────────
export default function Topbar({ title, subtitle, actions = [], search, onMenuClick }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // User dropdown
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Notification dropdown
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);

  const {
    unreadCount,
    notifications,
    loading: notifLoading,
    fetchNotifications,
    markRead,
    markAllRead,
  } = useNotifications();

  // Close user dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close notification dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleBellClick = () => {
    const opening = !notifOpen;
    setNotifOpen(opening);
    if (opening) fetchNotifications(); // fetch list only when opening
  };

  const userDisplayName = user?.name || (user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : "Sales Agent");
  const userRole = user?.role === "SUPER_ADMIN" ? "Super Admin"
    : user?.role === "ADMIN" ? "Admin"
    : user?.role === "MANAGER" ? "Manager"
    : "Sales Agent";
  const userInitials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : (user?.email ? user.email.substring(0, 2).toUpperCase() : "IZ");

  const handleProfileClick = () => {
    setDropdownOpen(false);
    navigate("/profile");
  };

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header
      className="flex items-center flex-shrink-0 gap-2 px-3 bg-white border-b md:gap-3 border-neutral-100 md:px-7"
      style={{ height: "56px", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}
    >
      {/* Mobile menu button */}
      {onMenuClick && (
        <button
          onClick={onMenuClick}
          className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg md:hidden text-neutral-600 hover:bg-neutral-50"
        >
          <Menu size={18} />
        </button>
      )}

      {/* Title */}
      <div className="flex flex-col justify-center flex-shrink min-w-0 pl-9 md:pl-0">
        <span className="text-sm font-bold truncate md:text-[15px] text-neutral-900 tracking-[-0.3px] leading-none">
          {title}
        </span>
        {subtitle && (
          <span className="hidden text-[10px] truncate md:inline-block md:text-[11px] text-neutral-400 mt-0.5 leading-none">
            {subtitle}
          </span>
        )}
      </div>

      <div className="flex-1" />

      {/* Search */}
      {search && (
        <div className="items-center hidden h-8 gap-2 px-3 border rounded-lg sm:flex bg-neutral-50 border-neutral-100 w-36 md:w-52">
          <Search size={12} className="flex-shrink-0 text-neutral-400" />
          <input
            type="text"
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder || "Search…"}
            className="w-full text-xs bg-transparent border-none outline-none text-neutral-700 placeholder-neutral-300"
          />
          {search.value && (
            <button onClick={() => search.onChange("")} className="flex-shrink-0 text-neutral-400 hover:text-neutral-600">
              <X size={11} />
            </button>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1 md:gap-2">
        {actions.map((action, i) => (
          <button
            key={i}
            onClick={action.onClick}
            className="flex items-center gap-1.5 px-2 md:px-3.5 h-8 rounded-lg text-xs font-semibold transition-colors"
            style={action.variant === "secondary"
              ? { background: "#f5f5f5", color: "#404040", border: "1px solid #e5e5e5" }
              : { background: "#009DB9", color: "#fff" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = action.variant === "secondary" ? "#ebebeb" : "#007a90"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = action.variant === "secondary" ? "#f5f5f5" : "#009DB9"; }}
          >
            {action.icon && <action.icon size={13} />}
            <span className="hidden sm:inline">{action.label}</span>
          </button>
        ))}
      </div>

      {/* ── Notification bell ── */}
      <div className="relative flex-shrink-0" ref={notifRef}>
        <button
          onClick={handleBellClick}
          className="relative flex items-center justify-center flex-shrink-0 w-8 h-8 transition-colors rounded-lg text-neutral-400 hover:bg-neutral-50"
          style={notifOpen ? { background: "rgba(0,157,185,0.08)", color: "#009DB9" } : {}}
        >
          <Bell size={16} />
          {unreadCount > 0 && (
            <span
              className="absolute flex items-center justify-center font-bold text-white rounded-full"
              style={{
                top: "4px",
                right: "4px",
                minWidth: unreadCount > 9 ? "16px" : "14px",
                height: "14px",
                fontSize: "9px",
                background: "#EF4444",
                lineHeight: 1,
                padding: "0 3px",
              }}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {notifOpen && (
          <NotificationDropdown
            notifications={notifications}
            loading={notifLoading}
            onMarkRead={markRead}
            onMarkAllRead={markAllRead}
            onClose={() => setNotifOpen(false)}
            navigate={navigate}
          />
        )}
      </div>

      {/* User profile with dropdown */}
      <div className="relative flex-shrink-0" ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="flex items-center flex-shrink-0 gap-1 pl-1 transition-colors border-l rounded-lg cursor-pointer md:gap-2 md:pl-2 border-neutral-100 hover:bg-neutral-50"
        >
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
            style={{ background: "#009DB9" }}
          >
            {userInitials}
          </div>
          <div className="flex-col hidden leading-none lg:flex">
            <span className="text-[12px] font-semibold text-neutral-800 truncate max-w-[100px]">{userDisplayName}</span>
            <span className="text-[10px] text-neutral-400">{userRole}</span>
          </div>
          <ChevronDown
            size={12}
            className="hidden transition-transform sm:block text-neutral-400"
            style={{ transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </button>

        {dropdownOpen && (
          <div
            className="absolute right-0 z-50 w-56 mt-2 overflow-hidden bg-white border top-full rounded-xl border-neutral-100"
            style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.10)" }}
          >
            <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50/60">
              <div className="flex items-center gap-2.5">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[12px] font-bold flex-shrink-0"
                  style={{ background: "#009DB9" }}
                >
                  {userInitials}
                </div>
                <div className="min-w-0">
                  <div className="text-[12px] font-bold text-neutral-800 truncate">{userDisplayName}</div>
                  <div className="text-[10px] text-neutral-400 truncate">{user?.email}</div>
                </div>
              </div>
            </div>

            <div className="py-1">
              <button
                onClick={handleProfileClick}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-[13px] text-neutral-700 hover:bg-neutral-50 transition-colors text-left"
              >
                <div
                  className="flex items-center justify-center rounded-lg w-7 h-7"
                  style={{ background: "rgba(0,157,185,0.08)" }}
                >
                  <User size={13} style={{ color: "#009DB9" }} />
                </div>
                <div>
                  <div className="font-medium text-[12px]">My Profile</div>
                  <div className="text-[10px] text-neutral-400">Account settings</div>
                </div>
              </button>

              <div className="my-1 border-t border-neutral-100" />

              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-[13px] text-red-500 hover:bg-red-50 transition-colors text-left"
              >
                <div className="flex items-center justify-center rounded-lg w-7 h-7 bg-red-50">
                  <LogOut size={13} className="text-red-500" />
                </div>
                <div>
                  <div className="font-medium text-[12px]">Logout</div>
                  <div className="text-[10px] text-red-400">End your session</div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}